import React, { useState, useEffect } from 'react';
import { FileAudio, Send, X, ChevronUp } from 'lucide-react';

const DEBUG_MODE = import.meta.env.VITE_VOICE_DEBUG_MODE === 'true';
const VOICE_DEBUG_FOLDER = '/voice-debug';

function VoiceDebugPanel({ onSendFile, isRecording = false }) {
    const [files, setFiles] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState(null);
    const [sendingFile, setSendingFile] = useState(null);

    useEffect(() => {
        if (!DEBUG_MODE) return;

        fetch(`${VOICE_DEBUG_FOLDER}/files.json`)
            .then(res => res.json())
            .then(data => {
                setFiles(data);
                setError(null);
            })
            .catch(err => {
                console.error('[VoiceDebug] Failed to load files.json:', err);
                setError('Không tìm thấy files.json');
            });
    }, []);

    if (!DEBUG_MODE) {
        return null;
    }

    const handleSendFile = async (file) => {
        if (!isRecording) {
            console.log('[VoiceDebug] Cannot send - not recording');
            return;
        }

        setSendingFile(file.file);
        try {
            const response = await fetch(`${VOICE_DEBUG_FOLDER}/${file.file}`);
            if (!response.ok) {
                throw new Error(`File not found: ${file.file}`);
            }
            const blob = await response.blob();
            await onSendFile?.(blob);
        } catch (err) {
            console.error('[VoiceDebug] Error:', err);
            setError(err.message);
        } finally {
            setSendingFile(null);
        }
    };

    return (
        <div className="fixed bottom-20 right-4 z-50">
            {isOpen && (
                <div className="mb-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-purple-600 text-white">
                        <span className="text-sm font-medium">Voice Debug</span>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="hover:bg-purple-700 rounded p-0.5"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {!isRecording && (
                        <div className="px-3 py-2 bg-yellow-50 border-b text-xs text-yellow-700">
                            ⚠️ Bấm mic để bắt đầu record trước
                        </div>
                    )}

                    <div className="p-2 max-h-72 overflow-y-auto">
                        {error ? (
                            <div className="text-center text-red-500 py-4 text-sm">
                                {error}
                            </div>
                        ) : files.length === 0 ? (
                            <div className="text-center text-gray-400 py-4 text-sm">
                                Không có file nào
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {files.map((file) => (
                                    <div
                                        key={file.file}
                                        className={`flex items-center gap-2 p-2 rounded ${
                                            sendingFile === file.file
                                                ? 'bg-purple-100'
                                                : 'bg-gray-50 hover:bg-gray-100'
                                        }`}
                                    >
                                        <FileAudio className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-gray-700 truncate">
                                                {file.name}
                                            </div>
                                            <div className="text-xs text-gray-400 truncate">
                                                {file.file}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleSendFile(file)}
                                            disabled={!isRecording || sendingFile !== null}
                                            className={`p-1.5 text-white rounded flex-shrink-0 ${
                                                isRecording && sendingFile === null
                                                    ? 'bg-purple-600 hover:bg-purple-700'
                                                    : 'bg-gray-300 cursor-not-allowed'
                                            }`}
                                        >
                                            <Send className={`w-3.5 h-3.5 ${sendingFile === file.file ? 'animate-pulse' : ''}`} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-500">
                        Folder: <code className="bg-gray-200 px-1 rounded">public/voice-debug/</code>
                    </div>
                </div>
            )}

            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-colors ${
                    isRecording
                        ? 'bg-green-500 text-white animate-pulse'
                        : isOpen
                            ? 'bg-purple-700 text-white'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
            >
                {isOpen ? (
                    <ChevronUp className="w-5 h-5" />
                ) : (
                    <FileAudio className="w-5 h-5" />
                )}
            </button>
        </div>
    );
}

export default VoiceDebugPanel;
